import ItemMedia from "./ItemMedia";

interface ModifierItem {
  id: number;
  orderBy?: number;
  modifierItem: {
    name: string;
    price: number;
  };
}

interface ModifierSet {
  id?: number;
  name: string;
  noOfRequired?: number;
  maximum?: number;
  orderBy?: number | null;
  modifierSetModifierItems?: ModifierItem[];
}

interface Product {
  id: number;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
  modifierSets?: ModifierSet[];
}

interface ItemDetailProps {
  product: Product;
  categoryName: string;
  onClose: () => void;
}

const sortModifierSets = (sets: ModifierSet[]): ModifierSet[] => {
  const isValid = (v: unknown) => v != null && v !== "" && v !== 999;
  return sets.slice().sort((a, b) => {
    const validA = isValid(a.orderBy);
    const validB = isValid(b.orderBy);
    if (validA && validB) {
      if (a.orderBy !== b.orderBy) return (a.orderBy as number) - (b.orderBy as number);
      return (a.name || "").localeCompare(b.name || "");
    }
    if (validA && !validB) return -1;
    if (!validA && validB) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
};

const ItemDetail = ({ product, categoryName, onClose }: ItemDetailProps) => {
  const modSets = sortModifierSets(product.modifierSets || []);

  return (
    <div className="detail">
      <div className="detail__hero">
        <ItemMedia
          src={product.imageUrl}
          name={product.name}
          itemKey={String(product.id ?? product.name)}
          categoryName={categoryName}
          variant="hero"
        />
      </div>
      <div className="detail__head">
        <p className="detail__eyebrow">{categoryName || "Menu item"}</p>
        <h2 className="detail__name">{product.name}</h2>
        {product.description && product.description !== "Null" && (
          <p className="detail__desc">{product.description}</p>
        )}
        <div className="detail__pricerow">
          <span className="detail__price">${product.price?.toFixed(2)}</span>
        </div>
      </div>

      {modSets.length > 0 ? (
        <div className="detail__mods">
          {modSets.map((s, idx) => {
            const required = (s.noOfRequired || 0) > 0;
            const multi = (s.maximum || 1) > 1;
            const ruleText = required
              ? `Required${multi ? ` · up to ${s.maximum}` : ""}`
              : `Optional${multi ? ` · up to ${s.maximum}` : ""}`;
            const items = (s.modifierSetModifierItems || [])
              .slice()
              .sort((a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0));
            return (
              <section key={s.id ?? idx} className="modset">
                <header className="modset__head">
                  <h4 className="modset__name">{s.name}</h4>
                  <span className="modset__rule">{ruleText}</span>
                </header>
                <ul className="modset__list">
                  {items.map((mi) => (
                    <li key={mi.id}>
                      <div className="modopt">
                        <span className="modopt__name">{mi.modifierItem?.name}</span>
                        {mi.modifierItem?.price > 0 && (
                          <span className="modopt__delta">+${mi.modifierItem.price.toFixed(2)}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : null}

      <button className="detail__close" onClick={onClose}>
        Close
      </button>
    </div>
  );
};

export default ItemDetail;
